import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { ProfileCatalogProduct, CatalogGroup } from '@/types';
import { getProfileCatalogProductsByGroup } from '@/services/profile-catalog-products';
import ProductSearch from '../products/ProductSearch';
import theme from './CatalogGroupAddProductDialog.module.scss';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { saveCatalogGroup } from '@/services/profile-catalog-groups';
import { findTopLevelCategory } from '@/utils/categories';
import { IState } from '@/state/IState';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import { useAppSelector, useGetActiveProfile } from '@/utils/hooks';

interface CatalogGroupAddProductDialogProps {
    group: CatalogGroup;
    show: boolean;
    onClose: () => void;
    onSave: () => void;
    onCancel?: () => void;
}

export const CatalogGroupAddProductDialog: React.FC<CatalogGroupAddProductDialogProps> = ({
    group,
    show,
    onCancel,
    onSave,
    onClose,
}) => {
    // const [groupProducts, setGroupProducts, reloadGroupProducts] = useAsyncState(
    //     () => getCatalogProductsByGroup(group.id),
    //     [group.id],
    //     null
    // );
    const itemCategories = useAppSelector((state) => state.global.itemCategories);
    const profileId = useGetActiveProfile()!.id;

    const [groupProducts, setGroupProducts] = useState<ProfileCatalogProduct[] | null>(null);
    const loadData = async () => {
        const products = await getProfileCatalogProductsByGroup(group.id);
        setGroupProducts(products.filter((p) => p.id > 0));
    };

    // const [productsToAdd, setProductsToAdd] = useState<CatalogProduct[]>([]);
    const [saving, setSaving] = useState(false);

    function reset() {
        setGroupProducts(null);
    }

    const handleCancelClick = () => {
        triggerHandler(onCancel);
        triggerHandler(onClose);
    };

    const handleSaveClick = async () => {
        setSaving(true);

        try {
            await saveCatalogGroup(
                group,
                groupProducts!.map((x) => x.id)
            );

            triggerHandler(onSave);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleProductCheckChange = (product: ProfileCatalogProduct, checked: boolean) => {
        const index = groupProducts!.findIndex((x) => x.id === product.id);
        if (index === -1 && checked) {
            setGroupProducts([...groupProducts!, product]);
        } else if (index > -1 && !checked) {
            setGroupProducts(groupProducts!.filter((x) => x.id !== product.id));
        }
    };

    const handleRemoveProductClick = (product: ProfileCatalogProduct) => {
        setGroupProducts(groupProducts!.filter((x) => x !== product));
    };

    const topLevelCategory =
        groupProducts && groupProducts[0] && groupProducts[0].itemCategoryId
            ? findTopLevelCategory(itemCategories, groupProducts[0].itemCategoryId)
            : null;

    return (
        <Modal
            isOpen={show}
            toggle={handleCancelClick}
            size="lg"
            onClosed={reset}
            onOpened={loadData}
            className={theme.dialog}
            autoFocus={false}>
            <ModalHeader>
                <Trans>Add / remove products</Trans>
            </ModalHeader>
            <ModalBody>
                {groupProducts ? (
                    <>
                        <ul className={theme.selectedProducts}>
                            {groupProducts.map((p) => (
                                <li key={p.id}>
                                    <span>{p.name}</span>
                                    <Button
                                        min
                                        variant="unstyled"
                                        icon={faTimes}
                                        onClick={(e) => handleRemoveProductClick(p)}
                                    />
                                </li>
                            ))}
                        </ul>

                        <ProductSearch
                            resultsClassName={theme.searchResults}
                            profileId={profileId}
                            checkedProductIds={groupProducts.map((x) => x.id)}
                            onCheckChange={handleProductCheckChange}
                            itemCategoryId={topLevelCategory ? topLevelCategory.id : null}
                        />
                    </>
                ) : (
                    <LoadingIndicator />
                )}
            </ModalBody>
            <ButtonBar>
                <ButtonBar.Right>
                    <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button variant="primary" loading={saving} onClick={handleSaveClick}>
                        <Trans>Save</Trans>
                    </Button>
                </ButtonBar.Right>
            </ButtonBar>
        </Modal>
    );
};
